/**
 * Die Bewegung, mit der ein Bereich **seine Höhe ändert** — auf, zu, und zwischen zwei
 * Größen, während er dasteht.
 *
 * **Unterschied zu `growMotion`:** Dort macht ein Element in einer Reihe Platz und
 * verschwindet wieder; das Maß ist beim Erscheinen 0 und am Ende `auto`. Hier bleibt der
 * Bereich stehen und wird nur länger oder kürzer — der Wiederkehr-Kasten tauscht beim
 * Wechsel von „Täglich" auf „Wöchentlich" eine Zeile gegen sieben Pillen.
 *
 * **Deshalb eine gemessene Zahl und nicht `auto`.** Ein Ziel, das `auto` bleibt, während
 * sich nur der Inhalt ändert, ist für Framer kein neuer Wert: Es fährt nichts. Gemessen
 * wird in der Hülle (`AutoHeight`), gerechnet hier.
 *
 * **Die Kurve ist gemessen, nicht gewählt** (19.09.2026, Storybook Bild für Bild). Der
 * erste Versuch mit steilem Anlauf (`[0.32, 0.72, 0, 1]`) nahm 80% der Höhe in den ersten
 * vier Bildern und ließ die letzten sechs Pixel über 100ms auslaufen — das las sich als
 * Ruck mit Nachlauf. Mit der Standardkurve des Hauses bilden die Schrittweiten eine
 * gleichmäßige Glocke: 1 · 4 · 12 · 21 · 32 · 35 · 27 · 22 · 15 · 12 · 9 · 6 · 4 · 3 · 2.
 *
 * **Die Deckkraft läuft linear und über dieselbe Zeit.** Auf der Höhenkurve war der Inhalt
 * nach 70ms unsichtbar, und der Kasten fuhr die restlichen zwei Drittel leer zu.
 *
 * Bewusst ohne DOM-Bezug: Hier wird gerechnet, in der Hülle eingesetzt, und geprüft ohne
 * gerendertes Bauteil.
 */

import type { TargetAndTransition } from 'framer-motion'

export interface HeightMotionOptions {
  /**
   * Die gemessene Höhe des Inhalts in Pixeln, oder `null`, solange nichts gemessen ist.
   *
   * `null` heißt `'auto'` — lieber keine Bewegung als ein Inhalt, der auf Höhe 0 geklemmt
   * wird. Das trifft einen Tab hinter `display: none` (CLAUDE.md, TabStack-Falle) und
   * jsdom, wo nichts Maße hat.
   */
  height: number | null
  /**
   * Der Flex-Abstand des Elternteils, den die Bewegung beim Zufahren tilgt.
   *
   * Eine Lücke bleibt stehen, während die Höhe schon 0 ist — `gap` fragt nicht, wie hoch
   * seine Nachbarn sind. Ein negativer unterer Rand nimmt sie weg. Vorgabe 0 für alles,
   * was nicht in einer Flex-Spalte mit Abstand steht.
   */
  collapseGap?: number
  /** Dauer. Vorgabe 0,26s — genug für rund 200px, ohne zu bummeln. */
  duration?: number
  /**
   * Schaltet die Bewegung ab — für `prefers-reduced-motion` und für schwebende Kästen, die
   * eine wachsende Höhe über ihren Anker schöbe. Die drei Zustände bleiben bestehen, damit
   * die Aufrufstelle nicht verzweigen muss.
   */
  reduced?: boolean
}

/** Was eine `motion`-Komponente braucht: drei Zustände. */
export interface HeightMotion {
  initial: TargetAndTransition
  animate: TargetAndTransition
  exit: TargetAndTransition
}

/** Die Standardkurve des Hauses — dieselbe, die `TaskEntry` und `DayGroup` schon fahren. */
const EASE = [0.4, 0, 0.2, 1] as const

/**
 * **Dieselbe Gangart, wenn etwas nicht wächst, sondern nur seinen Platz wechselt.**
 *
 * Für Framers `layout`-Animationen: Bricht eine Zeile um, wandert das Nachrückende an
 * seinen neuen Ort, statt dorthin zu springen. Höhe und Ortswechsel gehören zusammen —
 * liefen sie verschieden schnell, sähe eine Karte aus wie zwei Bewegungen.
 *
 * @example
 * <motion.div layout="position" transition={LAYOUT_SHIFT}>…</motion.div>
 */
export const LAYOUT_SHIFT = { duration: 0.26, ease: EASE } as const

/**
 * @example
 * const motion = heightMotion({ height: measured })
 * <motion.div initial={motion.initial} animate={motion.animate} exit={motion.exit} />
 *
 * @example
 * // In einer Flex-Spalte mit 16px Abstand, und ohne Bewegung bei reduzierter Bewegung:
 * heightMotion({ height, collapseGap: 16, reduced: useReducedMotion() ?? false })
 */
export function heightMotion({
  height,
  collapseGap = 0,
  duration = 0.26,
  reduced = false,
}: HeightMotionOptions): HeightMotion {
  const closed = { opacity: 0, height: 0, marginBottom: -collapseGap }
  const open = { opacity: 1, height: height ?? 'auto', marginBottom: 0 }

  if (reduced) {
    return {
      initial: closed,
      animate: { ...open, transition: { duration: 0 } },
      exit: { ...closed, transition: { duration: 0 } },
    }
  }

  const transition = { duration, ease: EASE, opacity: { duration, ease: 'linear' as const } }
  return {
    initial: closed,
    animate: { ...open, transition },
    exit: { ...closed, transition },
  }
}
